// Model report -> EKRM form: the mapping behind every wizard that builds one out of a model.
//
// Everything here is pure: it takes a parsed model report (the JSON described in
// docs/model-report-secciones.md) and returns plain numbers, so it can be exercised without Vue
// and without Firebase. The component decides what to do with them; nothing here writes state.
//
// Report shape this file relies on, all confirmed against ModelReportComponent.vue:
//   report.saturation[channel] = { x[], y[], regions[4] }   <- sibling of v2, not inside it
//   report.decay[channel]      = { x[], y[] }               <- likewise
//   report.v2.media_spend[channel]              = { x: dates, y: spend }
//   report.v2.model_fit.Real                    = { x: dates, y: kpi }
//   report.v2.seasonality[series]               = { x: dates, y: effect }
//   report.v2.sales_decomposition_total.components = [{ name, value }]
//   report.outputs[kpi]                         = the same set, per KPI
// `saturation` and `decay` may both be wrapped in a first-level 'Instant Marketing' group.

import { effectiveSaturation, defaultSaturationGroup, SATURATION_GROUP_KEY } from '@/utils/saturationSections.js'
import { regionsStrictlyIncreasing } from '@/utils/regions.js'
import { fitHill, scaleHill, hillSamples, gridSpends, curveGridPoint, HILL_FIT_TOLERANCE } from '@/utils/hillCurve.js'

export const CALENDAR_AVERAGE = 'calendarAverage'

// What the ten-point saturation grid is aligned to. CURVE_PRIORITY puts it where the source channel
// actually saturates, so the imported curve keeps the shape it has in its own model even when that
// sits far from the spend. Anything else leaves the grid in the same range as the media spend the
// model will be asked about, which is what the import used to do. Only the saturation point moves
// between the two — everything else about the import is identical.
export const CURVE_PRIORITY = 'curve'

// ---------------------------------------------------------------------------
// Report navigation
// ---------------------------------------------------------------------------

// saturation, decay and the whole v2 block repeat under outputs[kpi]; the root is the Total.
export function outputScope (report, kpiName) {
  if (!report) return null
  if (kpiName && kpiName !== 'Total' && report.outputs && report.outputs[kpiName]) {
    return report.outputs[kpiName]
  }
  return report
}

export function kpiNames (report) {
  if (!report || !report.outputs) return []
  return Object.keys(report.outputs)
}

function ungroup (data) {
  if (data && Object.prototype.hasOwnProperty.call(data, SATURATION_GROUP_KEY)) {
    return data[SATURATION_GROUP_KEY]
  }
  return data
}

export function saturationChannels (scope) {
  if (!scope || !scope.saturation) return {}
  return effectiveSaturation(scope.saturation, defaultSaturationGroup(scope.saturation, true)) || {}
}

export function decayChannels (scope) {
  if (!scope || !scope.decay) return {}
  return ungroup(scope.decay) || {}
}

export function spendChannels (scope) {
  if (!scope || !scope.v2 || !scope.v2.media_spend) return {}
  return scope.v2.media_spend
}

// A channel is importable when it has both a spend history and a saturation curve: without the
// first there is nothing to put in Media Spend, without the second nothing in Saturation.
export function importableChannels (scope) {
  const spend = spendChannels(scope)
  const saturation = saturationChannels(scope)
  return Object.keys(spend).filter(function (name) {
    return Object.prototype.hasOwnProperty.call(saturation, name)
  }).sort()
}

// ---------------------------------------------------------------------------
// Naming: report channels -> media
// ---------------------------------------------------------------------------

// A channel's display name, from the model's own dictionary.
//
// Firestore rejects a field name that is empty or wrapped in double underscores, and both are
// reachable from a pretty-names dictionary somebody edited. Nothing else is touched — a dot or a
// slash is a legal field name inside a map written whole with .add(), and rewriting it would rename
// a medium away from the channel it is copying for no gain.
export function displayName (model, raw) {
  const dictionary = model && model.pretty_names && model.pretty_names.media
  const pretty = (dictionary && dictionary[raw]) ? dictionary[raw] : raw
  let name = String(pretty === undefined || pretty === null ? '' : pretty).replace(/\s+/g, ' ').trim()
  if (name.length === 0) name = String(raw).trim()
  if (name.length === 0) return null
  if (/^__.*__$/.test(name)) name = name.slice(2, -2).trim() || null
  return name
}

// Raw channel keys -> the medium names the form and the payload use, paired with the channel each
// one came from.
//
// The medium name is a KEY in three maps of the definition — media_spend.own_controllable, decay and
// saturation — so two channels that share one pretty name do not merely look alike: the second
// overwrites the first and the EKRM is built with one channel fewer, silently, with the dropped
// channel's spend still counted in every total. A dictionary mapping two platforms onto one label is
// the ordinary way to reach that, and nothing downstream can detect it afterwards.
//
// The first claim wins, a collision falls back to the raw key, and a name still taken after that is
// suffixed. Deterministic, because importableChannels is sorted.
export function channelLabels (model, rawChannels) {
  const taken = {}
  const claim = function (name) {
    if (name === null || Object.prototype.hasOwnProperty.call(taken, name)) return null
    taken[name] = true
    return name
  }
  return (rawChannels || []).map(function (raw, index) {
    let label = claim(displayName(model, raw))
    if (label === null) label = claim(String(raw))
    let suffix = 2
    while (label === null) {
      label = claim(String(raw) + ' (' + suffix + ')')
      suffix++
      if (suffix > rawChannels.length + 2) label = claim('Channel ' + (index + 1) + ' (' + suffix + ')')
    }
    return { raw: raw, label: label }
  })
}

// ---------------------------------------------------------------------------
// Time: an arbitrary weekly/monthly history -> 12 Jan..Dec values, or an
// explicit window of 12*N months
// ---------------------------------------------------------------------------

// 'YYYY-MM-DD' is read straight off the string rather than through Date, which would shift the
// month across a timezone for anything at the edge of a month.
function monthKeyOf (value) {
  const asString = String(value)
  const match = asString.match(/^(\d{4})-(\d{2})/)
  if (match) return match[1] + '-' + match[2]
  const parsed = new Date(asString)
  if (isNaN(parsed.getTime())) return null
  const month = parsed.getMonth() + 1
  return parsed.getFullYear() + '-' + (month < 10 ? '0' + month : String(month))
}

// Totals per calendar month, plus how many source points landed in each — a weekly series has 4
// or 5 per month, and the partial months at either end of the history have fewer.
function bucketByMonth (series) {
  const totals = {}
  const counts = {}
  if (!series || !Array.isArray(series.x) || !Array.isArray(series.y)) {
    return { totals: totals, counts: counts }
  }
  for (let i = 0; i < series.x.length; i++) {
    const key = monthKeyOf(series.x[i])
    if (key === null) continue
    const value = Number(series.y[i])
    if (!isFinite(value)) continue
    totals[key] = (totals[key] || 0) + value
    counts[key] = (counts[key] || 0) + 1
  }
  return { totals: totals, counts: counts }
}

// A month is usable when it holds as many points as a full month of this series would. Monthly
// series have one point per month so everything qualifies; weekly ones need 4, since a calendar
// month spans 4 or 5 week-endings and demanding 5 would throw away most months.
function completeMonths (buckets) {
  const keys = Object.keys(buckets.totals)
  let maxCount = 0
  keys.forEach(function (key) {
    if (buckets.counts[key] > maxCount) maxCount = buckets.counts[key]
  })
  const required = Math.min(4, maxCount)
  return keys.filter(function (key) { return buckets.counts[key] >= required }).sort()
}

// The calendar years a series covers completely: every one of their twelve months survived
// completeMonths. Only the longest CONSECUTIVE run ending at the most recent complete year is
// returned, because the EKRM reads a multi-year series as consecutive years starting in January —
// a gap in the middle would silently shift every month after it into the wrong year.
//
// Strict about completeness on purpose. A month the series does not cover becomes a 0 in the
// payload, which for spend is a claim that nothing was spent and for the KPI is a hole in the
// history; neither is something to infer from an absence.
export function completeCalendarYears (series) {
  const usable = completeMonths(bucketByMonth(series))
  const monthsPerYear = {}
  usable.forEach(function (key) {
    const year = Number(key.slice(0, 4))
    if (!monthsPerYear[year]) monthsPerYear[year] = 0
    monthsPerYear[year] += 1
  })
  const full = Object.keys(monthsPerYear)
    .filter(function (year) { return monthsPerYear[year] === 12 })
    .map(Number)
    .sort(function (a, b) { return a - b })
  if (full.length === 0) return []

  // Walk back from the most recent complete year for as long as the years stay consecutive.
  const run = [full[full.length - 1]]
  for (let i = full.length - 2; i >= 0; i--) {
    if (full[i] !== run[0] - 1) break
    run.unshift(full[i])
  }
  return run
}

// The 'YYYY-MM' keys of those years, chronological — the window shape every reducer below takes.
export function monthsForYears (years) {
  const months = []
  ;(years || []).forEach(function (year) {
    for (let month = 1; month <= 12; month++) {
      months.push(year + '-' + (month < 10 ? '0' + month : String(month)))
    }
  })
  return months
}

// Collapses a dated series into 12 values indexed January..December, or onto an explicit window.
//
// With `months` — a list of 'YYYY-MM' keys — the series is read at those months and nothing is
// collapsed at all: the result is 12*N values in the window's own order, and `mode` does not apply.
// See the branch itself for why that path skips completeMonths.
//
// Without it, the collapsing mode: CALENDAR_AVERAGE averages every January together, every
// February together and so on across the whole history — a typical year, which is what
// "Avg. Monthly Revenue" asks for and what keeps seasonality intact. Months the history does not
// cover stay 0.
export function monthlyVector (series, mode, months) {
  const buckets = bucketByMonth(series)

  if (months && months.length > 0) {
    // Deliberately NOT filtered through completeMonths, unlike the two collapsing modes below.
    // That filter asks for min(4, maxCount) points per month OF THIS SERIES, which is the right
    // question when months are being averaged together — a half-covered month would drag its
    // calendar slot down. Here it would do the opposite of what this window is for: a flighted
    // channel that ran a single week in March has one point in that month, fails the test, and
    // would be written as zero — erasing the burst that verbatim months exist to preserve. The
    // window has already been validated as complete against the KPI, so per-series completeness
    // has nothing left to add. A month the series does not reach at all is 0, which is what lets
    // every series of a report be projected onto ONE window even though they cover different
    // spans — and the EKRM refuses a payload whose six monthly arrays are not all the same length.
    return months.map(function (key) {
      const total = buckets.totals[key]
      return isFinite(total) ? total : 0
    })
  }

  const usable = completeMonths(buckets)
  const result = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  if (usable.length === 0) return result

  const sums = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  const counts = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  usable.forEach(function (key) {
    const index = Number(key.slice(5, 7)) - 1
    sums[index] += buckets.totals[key]
    counts[index] += 1
  })
  for (let i = 0; i < 12; i++) {
    result[i] = counts[i] > 0 ? sums[i] / counts[i] : 0
  }
  return result
}

// The width every vector in this file comes out at, for the zero-filled fallbacks below.
function vectorLength (months) {
  return months && months.length > 0 ? months.length : 12
}

function zeroVector (months) {
  const result = []
  const length = vectorLength(months)
  for (let i = 0; i < length; i++) {
    result.push(0)
  }
  return result
}

// The same total spread evenly, which is what a model built from a budget rather than from a
// history carries. Not a collapse of the series to one number: it keeps the row's width, so a
// flattened channel still lines up with the KPI row and with every other channel beside it.
function flatVector (values) {
  if (!values || values.length === 0) return []
  let total = 0
  for (let i = 0; i < values.length; i++) {
    const value = Number(values[i])
    if (isFinite(value)) total += value
  }
  const each = total / values.length
  return values.map(function () { return each })
}

// The same collapse applied to every channel of a { channel: {x,y} } map and summed — the total
// own-controllable, co-marketing or competition line.
export function monthlyVectorTotal (seriesMap, mode, months) {
  const result = zeroVector(months)
  if (!seriesMap) return result
  Object.keys(seriesMap).forEach(function (key) {
    const vector = monthlyVector(seriesMap[key], mode, months)
    for (let i = 0; i < result.length; i++) {
      result[i] += vector[i]
    }
  })
  return result
}

export function kpiMonthly (scope, mode, months) {
  if (!scope || !scope.v2 || !scope.v2.model_fit || !scope.v2.model_fit.Real) {
    return zeroVector(months)
  }
  return monthlyVector(scope.v2.model_fit.Real, mode, months)
}

// ---------------------------------------------------------------------------
// Scaling
// ---------------------------------------------------------------------------

function mean (values) {
  if (!values || values.length === 0) return 0
  return values.reduce(function (a, b) { return a + b }, 0) / values.length
}

// The wizard asks for months; a model can be fitted on weeks. Both curves a report carries are per
// period, so both are converted with this — the decay half-life below and the saturation curve
// further down.
const WEEKS_PER_MONTH = 4.345

// ---------------------------------------------------------------------------
// Decay
// ---------------------------------------------------------------------------

function clamp (value, low, high) {
  return Math.min(high, Math.max(low, value))
}

// The resolution the two decay ranges are expressed in, in months, and the width of the interval a
// derived value is bracketed by. Whole months used to be the only option because the sliders that
// display these ranges only moved in whole months, and it cost more than it looked like it did:
// the EKRM multiplies the half-life window it is given by 1.5 at the top and 0.5 at the bottom
// before turning it into a prior, so a month of politeness here is three months of vagueness
// there.
const DECAY_RANGE_STEP = 0.25

// The interval of whole steps that contains a derived value, held inside [low, high]. A value that
// lands exactly on a step is opened by one, since a point estimate claims a confidence the
// derivation does not have and validateEkrmForm rejects a range whose ends are equal.
function bracketToStep (value, low, high) {
  const step = DECAY_RANGE_STEP
  const round = function (number) { return Math.round(number * 100) / 100 }
  let min = Math.floor(value / step) * step
  let max = Math.ceil(value / step) * step
  if (min === max) max = min + step
  min = clamp(min, low, high - step)
  max = clamp(max, min + step, high)
  return [round(min), round(max)]
}

// The report holds a decay CURVE, not the two ranges the wizard asks for, so both are read off
// its shape: the peak is where the curve is highest, the half-life where the CUMULATIVE response
// reaches half of the total.
//
// Cumulative, and not the first period at or below half the peak, because that is what the EKRM
// means by the word on the other side — compute_half_lives normalises the cumulative delay curve
// and takes the first time it crosses 0.5, and feasible_decay_check builds its feasible grid the
// same way. The two readings differ by about a month on these curves: a decay of 0.62, 0.26, 0.08
// has already delivered half its response partway through its own first period, so its half-life
// is 0.81 months and not 1, and sending [1, 2] for it made every channel come back slower than the
// model it was copied from. The crossing is interpolated inside the period it falls in, so a
// monthly curve can still say something other than a whole number.
//
// The maximum-effect slider is hard-coded to 0..2 months and the half-life cannot precede the peak,
// so both are clamped into those windows; the half-life's own ceiling is 3, which is where the
// blank form's default already sat. A wizard sizes its half-life slider to contain whatever comes
// back rather than the other way round.
// The same kernel on a monthly lag axis, for DRAWING it. A model fitted on weeks measured thirteen
// weekly lags; the EKRM being built is monthly and its own report will label that axis in months,
// so a card that drew the weekly lags would be showing an axis the created model never shows.
//
// This is a conversion of the PICTURE and not of the payload, which is untouched and still sends no
// weekly decay curve for the reason it never did: the monthly kernel is the weekly one convolved
// with the within-month spend spread, and re-binning is not that convolution. What makes the
// re-binning honest HERE is that it agrees with the thing that IS sent — decayRange reads its
// half-life off the cumulative response with the same WEEKS_PER_MONTH, so the month the drawn bars
// deliver half their mass in is the month the bracket the backend is given points at.
//
// Each week's weight is split across the months it overlaps rather than assigned to the month it
// starts in: 4.345 weeks to a month means most weeks straddle a boundary, and rounding each one
// whole would move mass around by up to half a week per lag and pile the error up along the tail.
// The total is preserved exactly, which is what lets the bars keep their shape.
export function monthlyDecayCurve (curve, granularity) {
  if (!curve || !Array.isArray(curve.y) || curve.y.length === 0) return curve || null
  if (granularity !== 'W') return curve
  const y = curve.y.map(Number)
  if (y.some(function (value) { return !isFinite(value) })) return null

  const months = Math.max(1, Math.ceil(y.length / WEEKS_PER_MONTH))
  const bins = new Array(months).fill(0)
  y.forEach(function (value, week) {
    // The [from, to) span this week covers on the month axis, in months.
    let from = week / WEEKS_PER_MONTH
    const to = (week + 1) / WEEKS_PER_MONTH
    while (from < to) {
      const bin = Math.min(months - 1, Math.floor(from))
      const edge = Math.min(to, bin + 1)
      bins[bin] += value * (edge - from) * WEEKS_PER_MONTH
      from = edge
    }
  })

  return Object.assign({}, curve, {
    x: bins.map(function (bin, index) { return index }),
    y: bins
  })
}

export function decayRange (curve, granularity) {
  const fallback = { maximumEffect: [0, 2], halfLife: [0, 3] }
  if (!curve || !Array.isArray(curve.y) || curve.y.length === 0) return fallback
  const y = curve.y.map(Number)
  if (y.some(function (v) { return !isFinite(v) })) return fallback
  const total = y.reduce(function (sum, value) { return sum + value }, 0)
  if (!(total > 0)) return fallback

  let peakIndex = 0
  for (let i = 1; i < y.length; i++) {
    if (y[i] > y[peakIndex]) peakIndex = i
  }
  const perPeriod = granularity === 'W' ? 1 / WEEKS_PER_MONTH : 1
  const peak = peakIndex * perPeriod

  // Where the running sum crosses half the total, as a fraction of the period it crosses in. The
  // loop always terminates on the last period, where the running sum is the total itself.
  const target = total / 2
  let running = 0
  let halfLife = (y.length - 1) * perPeriod
  for (let i = 0; i < y.length; i++) {
    if (running + y[i] >= target) {
      halfLife = (i + (y[i] > 0 ? (target - running) / y[i] : 0)) * perPeriod
      break
    }
    running += y[i]
  }

  const maximumEffect = bracketToStep(peak, 0, 2)
  const halfLifeRange = bracketToStep(Math.max(halfLife, maximumEffect[0]), maximumEffect[0], 3)

  return { maximumEffect: maximumEffect, halfLife: halfLifeRange }
}

// ---------------------------------------------------------------------------
// Saturation
// ---------------------------------------------------------------------------

// A saturation curve is drawn in spend PER PERIOD — that is what the model report's axis says
// ("Weekly Spend (USD)" when reference_model.config.saturation_granularity is 'W') and what the
// granularity column beside carry_cap/shape_u/shape_b in the platform's curve export records. The
// wizard's grid is monthly, so a weekly curve has to be stretched before anything reads it.
//
// Both axes, and by the same factor: spending S in a month is spending S/4.345 in each of its
// weeks, so the monthly response is 4.345 * R(S / 4.345). The regions travel with the x axis, being
// spend positions themselves; the datapoints are not used here and are carried across untouched.
export function toMonthlyCurve (channel, granularity) {
  if (!channel || granularity !== 'W') return channel
  const scaled = function (values) {
    if (!Array.isArray(values)) return values
    return values.map(function (value) { return Number(value) * WEEKS_PER_MONTH })
  }
  return Object.assign({}, channel, {
    x: scaled(channel.x),
    y: scaled(channel.y),
    regions: scaled(channel.regions)
  })
}

export function numericPairs (curve) {
  if (!curve || !Array.isArray(curve.x) || !Array.isArray(curve.y)) return []
  const pairs = []
  for (let i = 0; i < Math.min(curve.x.length, curve.y.length); i++) {
    const x = Number(curve.x[i])
    const y = Number(curve.y[i])
    if (isFinite(x) && isFinite(y) && x >= 0) {
      pairs.push([x, y])
    }
  }
  return pairs.sort(function (a, b) { return a[0] - b[0] })
}

// Linear interpolation over the curve's own points. Below the first point the curve is read as a
// straight line out of the origin (no spend, no response); above the last it is flat, which is
// what a saturated channel does and what the wizard's convergence check expects.
function interpolate (pairs, x) {
  if (pairs.length === 0) return 0
  if (x <= pairs[0][0]) {
    if (pairs[0][0] === 0) return pairs[0][1]
    return pairs[0][1] * (x / pairs[0][0])
  }
  const last = pairs[pairs.length - 1]
  if (x >= last[0]) return last[1]
  for (let i = 1; i < pairs.length; i++) {
    if (x <= pairs[i][0]) {
      const [x0, y0] = pairs[i - 1]
      const [x1, y1] = pairs[i]
      if (x1 === x0) return y1
      return y0 + (y1 - y0) * ((x - x0) / (x1 - x0))
    }
  }
  return last[1]
}

// The spend at which the channel stops responding — where the wizard's grid, which runs to
// 1.2x this value, should have flattened out.
//
// `regions[3]` is the platform's own answer: the boundary where the optimal zone gives way to
// over-investment. Failing that, the spend at which the curve first reaches 95% of the highest
// response it was observed to produce — which, for a channel the source model never pushed to
// saturation, lands near the top of the range it did explore. That is the honest answer: it says
// the knee is at least this far out, without extrapolating past the data. With neither, the largest
// month of spend, which at least keeps the grid where the money is.
//
// Both arguments are in the SOURCE model's own units, and the curve is expected to have been
// converted to months already. A caller rescaling the channel to another model's size multiplies
// the answer afterwards rather than handing in a scaled spend vector, which would compare a scaled
// spend against an unscaled curve.
export function curveKnee (channel, monthlySpend) {
  const maxSpend = monthlySpend && monthlySpend.length ? Math.max.apply(null, monthlySpend) : 0
  const pairs = numericPairs(channel)
  let point = null

  if (channel && Array.isArray(channel.regions) && channel.regions.length === 4 &&
      regionsStrictlyIncreasing(channel.regions.map(Number))) {
    point = Number(channel.regions[3])
  }

  if ((!point || !isFinite(point) || point <= 0) && pairs.length > 1) {
    const target = 0.95 * pairs[pairs.length - 1][1]
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i][1] >= target) {
        point = pairs[i][0]
        break
      }
    }
  }

  if (!point || !isFinite(point) || point <= 0) {
    point = maxSpend > 0 ? maxSpend : 1
  }
  return point
}

// The knee clamped into [maxSpend/2, 2*maxSpend]: outside that band the wizard warns that the grid
// does not cover the spend the model actually plans; when the grid is aligned to the spend rather
// than to the curve, the band is the harder constraint of the two.
export function saturationPoint (channel, monthlySpend) {
  const maxSpend = monthlySpend && monthlySpend.length ? Math.max.apply(null, monthlySpend) : 0
  const point = curveKnee(channel, monthlySpend)
  return maxSpend > 0 ? clamp(point, maxSpend / 2, 2 * maxSpend) : point
}

// The ten revenue values the wizard stores, read off the model's curve at the grid it derives
// from the saturation point. Forced non-decreasing because a decreasing curve is a hard error in
// the wizard and sampling noise in the source can produce a dip, and floored above zero because
// a zero response is a hard error too.
//
// `point` is in the source curve's own units and `scale` multiplies the response, which together
// give the stretched curve `factor * R(x / factor)`: a grid position read here is the same position
// of the rescaled channel the wizard ends up holding, multiplied by the factor. The ten positions
// come from gridSpends so this path and the parametric one describe the same ten spend levels.
export function saturationRevenue (channel, point, scale) {
  const pairs = numericPairs(channel)
  const factor = isFinite(scale) && scale > 0 ? scale : 1
  const values = []
  let running = 0
  gridSpends(point).forEach(function (x) {
    let value = interpolate(pairs, x) * factor
    if (!isFinite(value) || value < running) value = running
    running = value
    values.push(value)
  })
  const top = values[values.length - 1]
  const floor = top > 0 ? top / 1e6 : 1e-6
  return values.map(function (value) { return value > 0 ? value : floor })
}

// A curve in the shape the EKRM's own fitter reads it: two equal-length numeric arrays and
// nothing else. The report's `regions`, `datapoints` and `datapoints_dates` would ride into
// Firestore for nothing — the definition and the draft share one document — and the backend never
// looks at them. numericPairs also does the sorting and the coercion the other side would
// otherwise have to, and drops anything that is not a finite pair, so what leaves here cannot
// carry a NaN into the payload.
//
// `scale` stretches BOTH axes by default, which is the whole of the rescaling for a saturation
// curve: the stretched channel is `factor * R(x / factor)`, so the point (x, y) of the source
// becomes (factor*x, factor*y). A decay curve is unitless and passes 1.
//
// `revenueScale` splits the two apart, and defaults to `scale` so a resize still reads as one
// number. They differ only for a change of UNITS: the x axis is a spend and the y axis a response,
// and a model can carry them in two different currencies, of which only one may need converting.
//
// Fewer than two points is a ValueError on the other side, so such a curve comes back null and the
// medium falls back to the expert fields, which are sent beside it either way.
export function curvePayload (curve, scale, revenueScale) {
  const factor = isFinite(scale) && scale > 0 ? scale : 1
  const response = isFinite(revenueScale) && revenueScale > 0 ? revenueScale : factor
  const pairs = numericPairs(curve)
  if (pairs.length < 2) return null
  return {
    x: pairs.map(function (pair) { return pair[0] * factor }),
    y: pairs.map(function (pair) { return pair[1] * response })
  }
}

// ---------------------------------------------------------------------------
// Whole-model sections
// ---------------------------------------------------------------------------

function componentTotals (scope) {
  const decomposition = scope && scope.v2 && scope.v2.sales_decomposition_total
  const components = decomposition && Array.isArray(decomposition.components) ? decomposition.components : []
  return components.map(function (component) {
    return { name: String(component.name || '').toLowerCase(), value: Number(component.value) || 0 }
  })
}

function sumMatching (components, matcher) {
  return components.reduce(function (total, component) {
    return matcher(component.name) ? total + component.value : total
  }, 0)
}

// A derived share is a point estimate; the wizard wants a range. +/-3% of the value, and nothing
// more: adjust_attributions only corrects an attribution that falls OUTSIDE the band, and the
// component scale it rescales to correct one is a straight multiplier on every channel's saturation
// curve, so every point of width is a point in which the clone's curves are free to sit above or
// below the ones they were copied from. A marketing share of 4.35% widened by +/-20% gives
// [3.48, 5.21], inside which a model landing at 5.0 — 15% high on every channel at once — is never
// touched; at +/-3% it is [4.22, 4.48] and it is.
//
// Three and not two and a half, which is what the width has to clear: validateEkrmForm warns below
// max(5% of the mean, 0.1) and the two decimals kept here can round a 5%-wide band under it (a
// share of 2.5% comes out 0.12 across against a floor of 0.125). Six percent clears it at every
// value.
//
// The absolute floor is 0.12 for the same reason, one notch above the validator's 0.1 rather than
// equal to it: below a share of about 2 points the 6% is narrower than a tenth of a point and this
// floor is what the band ends up being, and two numbers meant to be exactly 0.1 apart are 0.1 apart
// in the wrong direction as often as not once they have been through two decimals. It also keeps
// min and max from meeting, which is a hard error.
// Exported because ekrmClone rewrites the marketing band when the user edits spend or revenue, and
// the rewritten band has to be the same shape as the imported one — same relative width, same floor
// — or an edit would change how much room the EKRM has to disagree with the number it was handed.
export function band (value) {
  const round = function (number) { return Math.round(number * 100) / 100 }
  let min = value * 0.97
  let max = value * 1.03
  if (max < min) {
    const swap = min
    min = max
    max = swap
  }
  const floor = 0.12
  if (max - min < floor) {
    const centre = (max + min) / 2
    min = centre - floor / 2
    max = centre + floor / 2
  }
  return { min: round(min), max: round(max) }
}

// Marketing and competition attribution as a share of modelled sales, read off the sales
// decomposition. Component names are free text in the report, so they are matched the way
// AiSummary already matches them: case-insensitive substring.
//
// Co-marketing has no counterpart in a model report, so it is returned as null and left blank in
// the form — where the wizard reads a blank as "pick a sensible value" rather than as zero.
export function attributionBands (scope) {
  const components = componentTotals(scope)
  const total = components.reduce(function (sum, component) { return sum + component.value }, 0)
  if (!isFinite(total) || total === 0) {
    return { ownControllable: null, comarketing: null, competition: null }
  }
  const share = function (matcher) { return 100 * sumMatching(components, matcher) / total }

  const marketing = share(function (name) {
    return name.indexOf('instant marketing') !== -1 ||
      name.indexOf('carry-over marketing') !== -1 ||
      name.indexOf('carryover marketing') !== -1 ||
      name === 'marketing_residual' ||
      name === 'marketing'
  })
  const competitionValue = share(function (name) { return name.indexOf('competition') !== -1 })

  const ownControllable = marketing > 0 ? band(marketing) : null
  let competition = null
  if (competitionValue !== 0) {
    // Competition attribution is negative by definition; the wizard rejects a positive value.
    const raw = band(competitionValue)
    competition = { min: Math.min(raw.min, 0), max: Math.min(raw.max, 0) }
    if (competition.min === competition.max) competition.min -= 3
  }
  return { ownControllable: ownControllable, comarketing: null, competition: competition }
}

function seasonalitySeries (scope) {
  const v2 = scope && scope.v2
  if (!v2) return null
  if (v2.seasonality && Object.keys(v2.seasonality).length > 0) {
    return v2.seasonality
  }
  // Reports with a drill-down decomposition drop the standalone series and carry seasonality as
  // one of the decomposable components instead.
  const byPeriod = v2.sales_decomposition_by_period
  if (!byPeriod) return null
  const pick = function (container) {
    if (!container) return null
    const key = Object.keys(container).find(function (name) {
      return String(name).toLowerCase().indexOf('seasonality') !== -1
    })
    return key ? { series: container[key] } : null
  }
  const direct = pick(byPeriod)
  if (direct) return direct
  const allVariablesKey = Object.keys(byPeriod).find(function (name) {
    return String(name).toLowerCase() === 'all_variables'
  })
  return allVariablesKey ? pick(byPeriod[allVariablesKey]) : null
}

// Periodic/cyclic events as the wizard wants them: what percentage of each month's KPI the
// seasonal effect accounts for. Clamped to +/-100, which the wizard enforces as a hard error.
//
// Spot events have no counterpart in a model report and are deliberately not invented here.
export function seasonalityVector (scope, kpiVector, mode, months) {
  const series = seasonalitySeries(scope)
  if (!series) return null
  const effect = monthlyVectorTotal(series, mode, months)
  const result = []
  let anyValue = false
  // Driven by the KPI vector's own length rather than by twelve: the two rows are divided against
  // each other and then sit side by side in the payload, so they cannot be different widths.
  for (let i = 0; i < kpiVector.length; i++) {
    const kpi = kpiVector[i]
    if (!isFinite(kpi) || kpi === 0) {
      result.push(0)
      continue
    }
    const percentage = clamp(100 * effect[i] / kpi, -100, 100)
    if (percentage !== 0) anyValue = true
    result.push(percentage)
  }
  return anyValue ? result : null
}

// Co-marketing and competition SPEND are not report outputs — a model only carries them if they
// were fed in as variables. inputs_over_time is where those land, keyed by the variable name the
// modeller chose, so they are matched on the name and summed across their categories.
export function inputSpendVector (scope, matcher, mode, months) {
  const inputs = scope && scope.v2 && scope.v2.inputs_over_time
  if (!inputs) return null
  const key = Object.keys(inputs).find(function (name) { return matcher(String(name).toLowerCase()) })
  if (!key) return null
  const categories = inputs[key] && inputs[key].categories
  if (!categories || Object.keys(categories).length === 0) return null
  const vector = monthlyVectorTotal(categories, mode, months)
  return vector.some(function (v) { return v !== 0 }) ? vector : null
}

export function isComarketingName (name) {
  return name.indexOf('comarketing') !== -1 || name.indexOf('co-marketing') !== -1 || name.indexOf('co marketing') !== -1
}

export function isCompetitionName (name) {
  return name.indexOf('competition') !== -1 || name.indexOf('competencia') !== -1
}

// The variation range the saturation grid carries, flat rather than derived from anything.
//
// On the other side this number is not a statement about the source model's uncertainty at all: it
// is the width of the jitter applied to the five copies of each curve the EKRM fits, and so the
// width of the prior box its carry_cap, shape_u and shape_b are then sampled from. Sent as the
// model's own error it left each channel's ceiling free to move a MAPE against the others for no
// reason anybody had chosen. One percent is a tolerance handed to a fitter, and narrower is closer
// to the curve that was imported. Never zero — the EKRM reads a falsy variation range as 100%.
export const SATURATION_VARIATION_RANGE = 1

// How wrong the model is, as a percentage — the honest width for a band around a figure the model
// produced, which is what the KPI and the seasonality rows want and the saturation grid turned out
// not to. MAPE is serialised either as a fraction or as a percentage depending on the report
// version, so a value at or below 1 is read as a fraction. Falls back to 10 when the report
// carries no quality block, which is the case for EKRM-derived models.
export function qualityVariationRange (scope) {
  const quality = scope && scope.v2 && scope.v2.model_quality
  const mape = quality ? Number(quality.MAPE) : NaN
  if (!isFinite(mape) || mape <= 0) return 10
  const percentage = mape <= 1 ? mape * 100 : mape
  return Math.round(clamp(percentage, 5, 40))
}

// Variation ranges have no counterpart in a model report either, but leaving them blank is a hard
// error for KPI and media spend, so they are derived from the dispersion of the series itself:
// a channel whose monthly spend swings widely gets a wider range. Bounded to 5..40 because the
// wizard warns above 50 and a range under 5 claims more precision than a derived number has.
//
// Over a multi-year window this measures the dispersion of the whole window, so it absorbs
// year-over-year growth on top of seasonality and comes out wider. That is the honest reading of
// the series that is actually being sent — the row genuinely varies by that much — and not a
// distortion to correct for.
export function variationRange (values) {
  const usable = (values || []).map(Number).filter(function (v) { return isFinite(v) })
  const average = mean(usable)
  if (!isFinite(average) || average === 0) return 10
  const variance = mean(usable.map(function (v) { return Math.pow(v - average, 2) }))
  const coefficient = 100 * Math.sqrt(variance) / Math.abs(average)
  if (!isFinite(coefficient)) return 10
  return Math.round(clamp(coefficient, 5, 40))
}

// ---------------------------------------------------------------------------
// Cell formatting
// ---------------------------------------------------------------------------

// Grid cells are strings — that is how jspreadsheet stores them and how the payload carries them.
// Values are rounded to two decimals, and to whole units once they are large enough that the
// decimals are noise, so the grids stay readable.
export function toCell (value) {
  const number = Number(value)
  if (!isFinite(number)) return ''
  const rounded = Math.abs(number) >= 1000 ? Math.round(number) : Math.round(number * 100) / 100
  return String(rounded)
}

export function toCells (values) {
  return values.map(toCell)
}

// The wizard rejects a saturation value of zero outright, and rounding is what can produce one: a
// response of 0.002 at the first grid point is a perfectly good number until toCell writes it as
// "0". Anything that rounds away is lifted to the smallest value the cell can carry, which keeps
// the row non-decreasing because only its leading values can be that small.
export function toRevenueCells (values) {
  return toCells(values).map(function (cell) {
    return Number(cell) > 0 ? cell : '0.01'
  })
}

// ---------------------------------------------------------------------------
// The two imports, in the shapes the grids hold
// ---------------------------------------------------------------------------

// Everything one medium takes from one channel of one model: its Media Spend row, its two decay
// ranges, its Saturation pair and the Hill parameters behind the curve. The caller only places
// these into the grids, so what the wizard ends up holding is exactly what a harness gets from
// calling this directly.
//
// The scale factor stretches BOTH axes. A source model half the size of the base one did not
// return its revenue at the base model's spend levels; it returned half the revenue at half the
// spend, so the curve is `factor * R(x / factor)` and not `factor * R(x)`. Everything read off the
// source therefore stays in the source's own units until the very end: the spend vector, the
// saturation point and the fitted parameters are each multiplied once, at the point where they
// leave this function.
//
// `revenueScale` is that same last multiplication for the RESPONSE axis alone, and defaults to
// `scale`, so a resize is still the one number it has always been. The two come apart only for a
// change of units: a model measured in EUR of spend against units sold has two axes in two
// denominations, and converting it to the USD an EKRM is always in takes one rate for each. Which
// figure takes which is set by what the figure IS, not by where it is read: spend cells, the
// largest month, the saturation point and K are spend; the ten grid values and Vmax are a response.
// The exponent, the variation range and the decay ranges are dimensionless and take neither.
//
// options: { mode, months, scale, revenueScale, granularity, saturationGranularity, priority },
// where `granularity` is the decay curve's period and `saturationGranularity` the saturation
// curve's — two separate settings on the model, and both are read as 'W' or 'M'.
//
// `months` widens the spend row to 12*N and nothing else: the decay ranges, the saturation point,
// the ten grid values, the Hill fit and both curves describe the channel's RESPONSE, which is one
// answer however many months of it were observed.
// Split in two, and the split is the whole reason the composer can resize a channel without paying
// for it. Everything expensive here — the monthly conversion, the Levenberg-Marquardt fit, the knee
// — runs on the source in the source's OWN units and does not read `scale` or `revenueScale` at
// all; the two factors are applied only in the linear tail below. So a wizard that re-imports
// because a level moved is re-running a fit whose input bytes and output bytes are provably
// identical. `readChannel` is that scale-independent half and is what such a wizard caches;
// `scaleChannelCells` is the tail, which is ten array passes over at most a few dozen points.
//
// The other thing it buys is ONE rounding, at the final magnitude. Multiplying the cells afterwards
// would round twice — and `toRevenueCells` floors at 0.01 and `toCell` changes precision at a
// thousand, so the second rounding is not a no-op — and it would break the grid-versus-point
// invariant the sampling below exists to keep.
export function readChannel (scope, channelName, options) {
  const opts = options || {}
  const mode = opts.mode || CALENDAR_AVERAGE
  const months = opts.months || null
  const spendSeries = spendChannels(scope)[channelName]
  const sourceCurve = saturationChannels(scope)[channelName]
  // Converted once, here, so everything downstream reads a curve in the source's own MONTHLY units
  // and the only conversion left to apply is the scale factor.
  const curve = toMonthlyCurve(sourceCurve, opts.saturationGranularity)
  const sourceSpend = monthlyVector(spendSeries, mode, months)

  // The curve is fitted in the source's units and the parameters rescaled, which is exact: the
  // exponent carries the shape and does not move, the ceiling and the knee carry the size and are
  // multiplied. Fitting the already-stretched curve would give the same answer with more noise.
  const fit = fitHill(numericPairs(curve))
  const usable = fit.ok && fit.rmsePct <= HILL_FIT_TOLERANCE

  // Which of the two the grid is aligned to. Under CURVE_PRIORITY the grid is run out to where the
  // response has flattened onto its ceiling, which is the only place the EKRM's refit can read the
  // ceiling from at all; the placement needs parameters worth trusting, so a channel whose fit was
  // rejected keeps the bare knee, which is still the honest answer.
  const knee = opts.priority === CURVE_PRIORITY
    ? (usable ? curveGridPoint(fit, curveKnee(curve, sourceSpend)) : curveKnee(curve, sourceSpend))
    : saturationPoint(curve, sourceSpend)

  const sourceDecay = decayChannels(scope)[channelName]

  return {
    hasSpend: !!spendSeries,
    hasSaturation: !!curve,
    curve: curve,
    sourceSpend: sourceSpend,
    sourceDecay: sourceDecay,
    fit: fit,
    usable: usable,
    knee: knee,
    decay: decayRange(sourceDecay, opts.granularity),
    // Unitless, so no factor — but the lag axis is in the SOURCE model's own periods and the
    // backend reads it as months, so a weekly model's thirteen lags would arrive as thirteen
    // months. Only a monthly source sends a curve; a weekly one keeps the bracketed ranges alone,
    // which is what per-medium detection on the other side allows. Re-binning weeks into months is
    // not the conversion — the monthly kernel is the weekly one convolved with the within-month
    // spend spread — so it is deliberately not attempted.
    decayCurve: opts.granularity === 'M' ? curvePayload(sourceDecay, 1) : null
  }
}

// The linear tail: one multiplication per axis, then the cells.
//
// options: { scale, revenueScale, flatten, monthlySpend }. The last two are the composer's and both
// concern the spend row alone. `flatten` replaces the twelve months with twelve copies of their
// mean — a model built from scratch states a budget, not a calendar, and a channel lifted out of one
// model's year has no claim on another's.
//
// `monthlySpend` goes further and REPLACES the row outright with twelve copies of one figure, given
// in the payload's own units and therefore untouched by `scale`. It is what a wizard passes when the
// channel's own budget is not part of what is being imported: the curve says what a level of spend
// buys, the source's spending says what that one business chose to spend, and those are two
// different claims. A wizard that states the budget itself takes the first and leaves the second
// behind.
//
// The variation range is deliberately measured on the spend as the SOURCE spent it, before either of
// them. It is how far the fitter may move the row rather than a part of what the row says, and a flat
// row's own dispersion is zero, which would clamp to the floor and declare a confidence about the
// monthly split that nobody has.
export function scaleChannelCells (read, options) {
  const opts = options || {}
  const factor = isFinite(opts.scale) && opts.scale > 0 ? opts.scale : 1
  const revenueFactor = isFinite(opts.revenueScale) && opts.revenueScale > 0 ? opts.revenueScale : factor

  const spend = read.sourceSpend.map(function (value) { return value * factor })
  const stated = Number(opts.monthlySpend)
  const cellSpend = isFinite(stated) && stated > 0
    ? spend.map(function () { return stated })
    : (opts.flatten ? flatVector(spend) : spend)

  const hill = scaleHill(read.fit, factor, revenueFactor)

  // Sampled at the ROUNDED point, the one the grid ends up holding and the one
  // buildEkrmDefinition regenerates the payload's spend array from, so the ten stored values and
  // the ten spend levels they answer for can never be a rounding apart.
  const saturationPointCell = toCell(read.knee * factor)
  const point = Number(saturationPointCell) > 0 ? Number(saturationPointCell) : 1

  // A curve the Hill function cannot describe keeps the values sampled straight off it. Replacing
  // it with a poor fit would hand the user sliders that quietly misreport their own source.
  const revenue = read.usable
    ? hillSamples(hill, point)
    : saturationRevenue(read.curve, point / factor, revenueFactor)

  return {
    hasSpend: read.hasSpend,
    hasSaturation: read.hasSaturation,
    spendVariationRange: String(variationRange(spend)),
    spendCells: toCells(cellSpend),
    decay: read.decay,
    revenueVariationRange: String(SATURATION_VARIATION_RANGE),
    maximumSpendCell: toCell(Math.max.apply(null, cellSpend)),
    saturationPointCell: saturationPointCell,
    revenueCells: toRevenueCells(revenue),
    // The sliders start here whether or not the grid was generated from them; a channel that kept
    // its sampled values still has parameters to move, it just has to adopt them first.
    hill: hill,
    hillFitPct: read.fit.rmsePct,
    curveMode: read.usable ? 'hill' : 'sampled',
    // The two curves themselves, which the EKRM now fits directly rather than re-inferring from the
    // grid and the two decay brackets. Everything above is still produced and still sent: the grid
    // and the sliders are what the user reads and edits, they are what a medium falls back to the
    // moment one of them is touched, and check_ekrm_correctness reads the expert keys off the raw
    // payload regardless.
    //
    // The saturation curve is already monthly and in the source's own units, so it takes the same
    // multiplications everything else here takes — one per axis, which are the same number for a
    // resize and two different ones for a change of units.
    saturationCurve: curvePayload(read.curve, factor, revenueFactor),
    // How much of that curve was MEASURED, at this size. The composer puts the fit's tail back on a
    // source curve before importing it, and the card clips its sparkline to what the source model
    // actually saw; without this the two are indistinguishable once they are one array. Absent for a
    // curve nothing was appended to, where the last point is the answer and the caller falls back.
    measuredSpan: isFinite(read.measuredSpan) && read.measuredSpan > 0
      ? read.measuredSpan * factor
      : null,
    decayCurve: read.decayCurve
  }
}

export function importChannelCells (scope, channelName, options) {
  return scaleChannelCells(readChannel(scope, channelName, options), options)
}

// Everything that describes the whole model rather than one channel, all from the base model so
// the sections agree with each other. A null means the report has no counterpart for that row and
// it must be left blank rather than zeroed — the wizard reads a blank as "choose a sensible
// value" and a zero as a claim.
//
// The two scales are for a change of units and default to 1, which is what a wizard with a BASE
// model means: there these rows come from that model, whose units are the ones everything else is
// being converted INTO, so there is nothing left to convert. A wizard that takes them from a
// source model instead has to carry them across with it. `revenueScale` is the KPI, `spendScale` the co-marketing and
// competition spends. Nothing else here moves: seasonality is a percentage of the KPI and the
// attribution bands are shares of modelled sales, so both are ratios whose units have already
// cancelled, and every variation range is a coefficient of variation, which a multiplier cannot
// touch.
//
// Seasonality is derived from the UNCONVERTED KPI on purpose. It divides an effect measured in the
// source's own units by that same KPI, and handing it a converted denominator would divide two
// currencies against each other.
export function importGlobalCells (scope, options) {
  const opts = options || {}
  const mode = opts.mode || CALENDAR_AVERAGE
  const months = opts.months || null
  const revenueScale = isFinite(opts.revenueScale) && opts.revenueScale > 0 ? opts.revenueScale : 1
  const spendScale = isFinite(opts.spendScale) && opts.spendScale > 0 ? opts.spendScale : 1
  const quality = String(qualityVariationRange(scope))
  const kpiVector = kpiMonthly(scope, mode, months)
  const seasonality = seasonalityVector(scope, kpiVector, mode, months)
  const comarketing = inputSpendVector(scope, isComarketingName, mode, months)
  const competition = inputSpendVector(scope, isCompetitionName, mode, months)
  const bands = attributionBands(scope)
  const converted = function (values, scale) {
    return scale === 1 ? values : values.map(function (value) { return value * scale })
  }

  return {
    kpiVariationRange: quality,
    kpiCells: toCells(converted(kpiVector, revenueScale)),
    seasonalityVariationRange: quality,
    seasonalityCells: seasonality ? toCells(seasonality) : null,
    comarketingVariationRange: comarketing ? String(variationRange(comarketing)) : null,
    comarketingCells: comarketing ? toCells(converted(comarketing, spendScale)) : null,
    competitionVariationRange: competition ? String(variationRange(competition)) : null,
    competitionCells: competition ? toCells(converted(competition, spendScale)) : null,
    ownControllableBand: bands.ownControllable,
    // A component declared in Attributions but absent from Media Spend is reported by the wizard
    // as inconsistent, and rightly so, so the competition band only travels with its spend.
    competitionBand: competition ? bands.competition : null
  }
}
